import {
  EditNote,
  EventBusy,
  HourglassEmpty,
  MoveToInbox,
  Send,
} from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DateRangeIcon from "@mui/icons-material/DateRange";
import TodayIcon from "@mui/icons-material/Today";
import { Box, Chip, Stack, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

export type FilterName =
  | "isMine"
  | "isEditable"
  | "isToday"
  | "isWeek"
  | "isRequestsToMe"
  | "isPendingMyApproval"
  | "isExpiringSoon"
  | "isExpired";

export type FilterState = Partial<Record<FilterName, boolean>>;

export type QuickFilterChip = {
  name: FilterName;
  label: string;
  icon: React.ReactElement;
};

// Mapped type ensures each key's `name` must match its key (e.g. isMine: { name: "isMine" })
type DefaultChipMap = {
  [K in FilterName]: Omit<QuickFilterChip, "name"> & { name: K };
};

export const DEFAULT_CHIPS: DefaultChipMap = {
  isMine: { name: "isMine", label: "Submitted by me", icon: <Send /> },
  isEditable: {
    name: "isEditable",
    label: "Read/Write Access",
    icon: <EditNote />,
  },
  isToday: { name: "isToday", label: "Today", icon: <TodayIcon /> },
  isWeek: { name: "isWeek", label: "This week", icon: <DateRangeIcon /> },
  isRequestsToMe: {
    name: "isRequestsToMe",
    label: "Requests to me",
    icon: <MoveToInbox />,
  },
  isPendingMyApproval: {
    name: "isPendingMyApproval",
    label: "Pending my approval",
    icon: <HourglassEmpty />,
  },
  isExpiringSoon: {
    name: "isExpiringSoon",
    label: "Expiring soon",
    icon: <AccessTimeIcon />,
  },
  isExpired: {
    name: "isExpired",
    label: "Expired",
    icon: <EventBusy />,
  },
};

interface QuickFiltersProps {
  filters: QuickFilterChip[];
  filterState?: FilterState;
  setQuickFilter: (filter: {
    filterName: FilterName;
    isSelected: boolean;
  }) => void;
  columnsButton?: React.ReactNode;
  /** Rendered first in the chip row, before `filters` — for chips not backed by FilterName/filterState. */
  leadingChip?: React.ReactNode;
}

const QuickFiltersContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flexWrap: "wrap",
  marginBottom: theme.spacing(1.5),
}));

const FiltersSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const ColumnsSection = styled(Box)(() => ({
  marginLeft: "auto",
}));

function QuickFilters({
  filters,
  filterState,
  setQuickFilter,
  columnsButton,
  leadingChip,
}: QuickFiltersProps) {
  const [selectedFilters, setSelectedFilters] = useState<FilterState>(
    filterState || {},
  );
  useEffect(() => {
    if (filterState) {
      setSelectedFilters(filterState);
    }
  }, [filterState]);

  const handleChipToggle = (filter: FilterName) => {
    setSelectedFilters((prev: FilterState) => ({
      ...prev,
      [filter]: !prev[filter],
    }));
    setQuickFilter({
      filterName: filter,
      isSelected: !selectedFilters[filter],
    });
  };

  return (
    <QuickFiltersContainer>
      <FiltersSection>
        <Typography variant="body2">Quick filters</Typography>
        <Stack direction="row" spacing={1}>
          {leadingChip}
          {filters.map((chip) => (
            <Chip
              key={chip.name}
              label={chip.label}
              icon={chip.icon}
              clickable
              variant="filled"
              color={selectedFilters[chip.name] ? "primary" : "default"}
              onClick={() => handleChipToggle(chip.name)}
              sx={{ fontSize: "0.75rem" }}
            />
          ))}
        </Stack>
      </FiltersSection>
      {columnsButton && <ColumnsSection>{columnsButton}</ColumnsSection>}
    </QuickFiltersContainer>
  );
}

export { QuickFilters };
